import apiService from '@/services/apiService';

const state = {
  assessments: [],
  currentAssessment: null,
  currentAssessmentResponse: null,
  templates: [],
  templateCategories: [],
  loading: false,
  templatesLoading: false,
  error: null,
  pagination: {
    totalPages: 1,
    totalItems: 0,
    currentPage: 1,
    pageSize: 10
  },
  filters: {
    searchTerm: '',
    status: null,
    isActive: true
  },
  templateDetails: {}
};

const getters = {
  allAssessments: state => state.assessments,
  currentAssessment: state => state.currentAssessment,
  availableTemplates: state => state.templates,
  templateCategories: state => state.templateCategories,
  
  newAssessments: state => state.assessments.filter(a => a.completion_percentage === 0),
  
  // Ongoing = any non-draft assessment that is not yet 100 % completed
  ongoingAssessments: state => state.assessments.filter(a => {
    const pct = a.completion_percentage ?? 0;
    return !a.is_draft && pct < 100;
  }),
  
  completedAssessments: state => state.assessments.filter(a => a.completion_percentage === 100),
  
  draftAssessments: state => state.assessments.filter(a => a.is_draft),
  
  filteredAssessments: (state, getters) => {
    // 1) Base list depending on the status filter
    let base = state.assessments;

    if (state.filters.status) {
      switch (state.filters.status) {
        case 'new':
          base = getters.newAssessments;
          break;
        case 'ongoing':
          base = getters.ongoingAssessments;
          break;
        case 'completed':
          base = getters.completedAssessments;
          break;
        case 'drafts':
          base = getters.draftAssessments;
          break;
        default:
          base = state.assessments;
      }
    }

    // 2) Apply the searchTerm filter on that subset – case-insensitive title match
    if (state.filters.searchTerm) {
      const term = state.filters.searchTerm.toLowerCase();
      base = base.filter(a => (a.title || '').toLowerCase().includes(term));
    }

    return base;
  },
  
  getTemplateDetails: state => id => state.templateDetails[id]
};

const actions = {
  // Fetch all assessments
  async fetchAssessments({ commit, state }) {
    commit('SET_LOADING', true);
    try {
      const params = {};
      
      // Add search term if available
      if (state.filters.searchTerm) {
        params.search = state.filters.searchTerm;
      }
      
      // Add active filter if set
      if (state.filters.isActive !== null) {
        params.is_active = state.filters.isActive;
      }
      
      const response = await apiService.getAssessments(params);
      // Backend returns full list (no pagination)
      commit('SET_ASSESSMENTS', Array.isArray(response.data) ? response.data : (response.data?.results || []));
      
      return state.assessments;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch assessments');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Template Actions
  async fetchAssessmentTemplates({ commit }, params = {}) {
    commit('SET_TEMPLATES_LOADING', true);
    try {
      const response = await apiService.getAssessmentTemplates(params);
      const templates = response.data?.results || response.data || [];
      commit('SET_TEMPLATES', templates);
      return templates;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch assessment templates');
      throw error;
    } finally {
      commit('SET_TEMPLATES_LOADING', false);
    }
  },

  async fetchAssessmentTemplate({ commit }, id) {
    commit('SET_TEMPLATES_LOADING', true);
    try {
      const response = await apiService.getAssessmentTemplate(id);
      const templateDetails = response.data;
      commit('SET_TEMPLATE_DETAILS', { id, details: templateDetails });
      return templateDetails;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to fetch template with ID ${id}`);
      throw error;
    } finally {
      commit('SET_TEMPLATES_LOADING', false);
    }
  },

  async fetchTemplateCategories({ commit }) {
    try {
      const response = await apiService.getTemplateCategories();
      const categories = response.data?.categories || [];
      commit('SET_TEMPLATE_CATEGORIES', categories);
      return categories;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch template categories');
      throw error;
    }
  },

  async createAssessmentTemplate({ commit }, templateData) {
    commit('SET_TEMPLATES_LOADING', true);
    try {
      const response = await apiService.createAssessmentTemplate(templateData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create assessment template');
      throw error;
    } finally {
      commit('SET_TEMPLATES_LOADING', false);
    }
  },

  async updateAssessmentTemplate({ commit }, { id, templateData }) {
    commit('SET_TEMPLATES_LOADING', true);
    try {
      const response = await apiService.updateAssessmentTemplate(id, templateData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to update template with ID ${id}`);
      throw error;
    } finally {
      commit('SET_TEMPLATES_LOADING', false);
    }
  },

  async deleteAssessmentTemplate({ commit, dispatch }, id) {
    commit('SET_TEMPLATES_LOADING', true);
    try {
      await apiService.deleteAssessmentTemplate(id);
      // Reload templates list after deletion
      await dispatch('fetchAssessmentTemplates');
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to delete template with ID ${id}`);
      throw error;
    } finally {
      commit('SET_TEMPLATES_LOADING', false);
    }
  },

  // Fetch single assessment by ID
  async fetchAssessment({ commit }, id) {
    commit('SET_LOADING', true);
    try {
      // Use the default detail endpoint to get complete assessment details
      const response = await apiService.get(`/assessments/${id}/`);
      commit('SET_CURRENT_ASSESSMENT', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to fetch assessment with ID ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Alias for fetchAssessment to maintain compatibility with component naming
  async fetchAssessmentById({ dispatch }, id) {
    return dispatch('fetchAssessment', id);
  },
  
  // Create new assessment
  async createAssessment({ commit }, assessmentData) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.createAssessment(assessmentData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create assessment');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Update existing assessment
  async updateAssessment({ commit }, { id, assessmentData }) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.updateAssessment(id, assessmentData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to update assessment with ID ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Delete assessment
  async deleteAssessment({ commit, dispatch }, id) {
    commit('SET_LOADING', true);
    try {
      await apiService.deleteAssessment(id);
      // Reload assessments list after deletion
      await dispatch('fetchAssessments');
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to delete assessment with ID ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Activate/deactivate assessment
  async toggleAssessmentActive({ commit }, { id, isActive }) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.updateAssessment(id, { is_active: isActive });
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || `Failed to update active status for assessment with ID ${id}`);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Create a question
  async createQuestion({ commit }, questionData) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.createQuestion(questionData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create question');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Create question visual material link
  async createQuestionVisMaterial({ commit }, data) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.createQuestionVisMaterial(data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to link visual material to question');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // NEW ACTIONS FOR ASSESSMENT COMPLETION
  
  // Fetch assessment response by assessment ID and expert ID (or current user)
  async fetchAssessmentResponse({ commit }, { assessment_id, is_complete = null }) {
    commit('SET_LOADING', true);
    try {
      const params = { assessment_id };
      if (is_complete !== null) {
        params.is_complete = is_complete;
      }
      
      const response = await apiService.getAssessmentResponses(params);
      
      // Handle paginated response and get first matching response
      let assessmentResponse = null;
      if (response.data && response.data.results && response.data.results.length > 0) {
        assessmentResponse = response.data.results[0];
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        assessmentResponse = response.data[0];
      }
      
      if (assessmentResponse) {
        commit('SET_CURRENT_ASSESSMENT_RESPONSE', assessmentResponse);
      }
      
      return assessmentResponse;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch assessment response');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Create new assessment response
  async createAssessmentResponse({ commit }, responseData) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.createAssessmentResponse(responseData);
      commit('SET_CURRENT_ASSESSMENT_RESPONSE', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to create assessment response');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Save question response
  async saveQuestionResponse({ commit }, responseData) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.saveQuestionResponse(responseData);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to save question response');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Mark assessment response as complete
  async completeAssessmentResponse({ commit }, responseId) {
    commit('SET_LOADING', true);
    try {
      const response = await apiService.completeAssessmentResponse(responseId);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to complete assessment response');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_TEMPLATES_LOADING(state, loading) {
    state.templatesLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_ASSESSMENTS(state, assessments) {
    state.assessments = assessments;
  },
  SET_TEMPLATES(state, templates) {
    state.templates = templates;
  },
  SET_TEMPLATE_CATEGORIES(state, categories) {
    state.templateCategories = categories;
  },
  SET_TEMPLATE_DETAILS(state, { id, details }) {
    // Store template details in a map for efficient lookup
    if (!state.templateDetails) {
      state.templateDetails = {};
    }
    state.templateDetails[id] = details;
  },
  SET_CURRENT_ASSESSMENT(state, assessment) {
    state.currentAssessment = assessment;
  },
  SET_CURRENT_ASSESSMENT_RESPONSE(state, response) {
    state.currentAssessmentResponse = response;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  SET_FILTER(state, { key, value }) {
    state.filters[key] = value;
  },
  RESET_FILTERS(state) {
    state.filters = {
      searchTerm: '',
      status: null,
      isActive: true
    };
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}; 